import { useState } from "react"
import { Image as ImageIcon, Play, Video } from "lucide-react"
import { AttachmentViewer } from "./AttachmentViewer"
import { cn } from "@/lib/utils"

// Media thumbnail shown on piece cards (calendar week/month/list, pipeline).
// Prefers a still image when present; otherwise, for a video attachment,
// renders a generic rectangular thumb with a play button that opens the
// video in the same modal used on the piece detail page.
//
// Layouts:
//   • "grid" — full-width block below the card content (calendar week,
//     pipeline cards).
//   • "list" — fixed rectangle to the right of the content (calendar list).
//   • "icon" — no thumbnail at all; a small image/video icon button that
//     opens the media in the viewer modal (calendar month, where cell
//     space is tight).
type Props = {
  imageUrl?: string | null
  videoUrl?: string | null
  videoFilename?: string | null
  layout?: "grid" | "list" | "icon"
}

export function CardMedia({ imageUrl, videoUrl, videoFilename, layout = "grid" }: Props) {
  if (layout === "icon") {
    if (videoUrl) return <MediaIcon url={videoUrl} filename={videoFilename ?? null} kind="video" />
    if (imageUrl) return <MediaIcon url={imageUrl} filename={null} kind="image" />
    return null
  }
  if (videoUrl) {
    return <VideoThumb url={videoUrl} filename={videoFilename ?? null} layout={layout} />
  }
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        draggable={false}
        className={cn(
          "rounded object-cover",
          layout === "list" ? "h-12 w-20 shrink-0" : "mt-1.5 aspect-video w-full",
        )}
      />
    )
  }
  return null
}

// Compact media affordance for the "icon" layout: a small image/video icon
// that launches the same AttachmentViewer modal the thumbs use, without
// taking up thumbnail real estate on the card.
function MediaIcon({
  url,
  filename,
  kind,
}: {
  url: string
  filename: string | null
  kind: "image" | "video"
}) {
  const [open, setOpen] = useState(false)
  const Icon = kind === "video" ? Video : ImageIcon

  return (
    <>
      <button
        type="button"
        aria-label={kind === "video" ? "View video" : "View image"}
        title={kind === "video" ? "View video" : "View image"}
        // Stop the click from reaching the surrounding card (which would
        // navigate / open the piece preview). preventDefault also cancels
        // the parent <a>'s default navigation.
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(true)
        }}
        className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded text-ink-muted transition-colors hover:bg-page/70 hover:text-ink-display"
      >
        <Icon className="h-3.5 w-3.5" />
      </button>
      <AttachmentViewer url={url} filename={filename} kind={kind} open={open} onOpenChange={setOpen} />
    </>
  )
}

function VideoThumb({
  url,
  filename,
  layout,
}: {
  url: string
  filename: string | null
  layout: "grid" | "list"
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        aria-label={filename ? `Play ${filename}` : "Play video"}
        // Stop the click from reaching the surrounding card (which would
        // navigate / open the piece preview). preventDefault also cancels the
        // parent <a>'s default navigation. The modal is opened via our own
        // state, so we don't depend on a Radix trigger here.
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(true)
        }}
        className={cn(
          "group relative flex items-center justify-center overflow-hidden rounded bg-ink-display/85",
          layout === "list" ? "h-12 w-20 shrink-0" : "mt-1.5 aspect-video w-full",
        )}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-page/90 transition-transform group-hover:scale-110">
          <Play className="h-3.5 w-3.5 translate-x-px fill-ink-display text-ink-display" />
        </span>
      </button>
      <AttachmentViewer url={url} filename={filename} kind="video" open={open} onOpenChange={setOpen} />
    </>
  )
}
